/**
 * Klaverjassen. The rules, and nothing else.
 *
 * Pure functions over cards, like the rest of `game-logic` — but sharing far
 * less with the other two games than hartenjagen did, and it is worth saying
 * why up front:
 *
 *  - The deck is **32 cards**, seven to ace.
 *  - The order is **not numeric**. In trump the jack beats the ace and the nine
 *    beats the ten; everywhere the ten beats the king. So `trickWinner()` from
 *    `trick.ts`, which compares `card.rank`, is wrong here and is deliberately
 *    not used — `klaverjasTrickWinner` exists instead.
 *  - A trick is worth its **card points**, not one.
 *  - There are two fixed **teams**, seats 0+2 against 1+3.
 *
 * What is reused unchanged: `shuffle`, `cardsEqual`, `removeCard`, `hasCard`.
 */

import type { Card, Rank, Suit, Trick } from '@shared/types/game';
import { SUITS } from '@shared/types/game';
import { cardsEqual, shuffle, type Rng } from './deck';

export const KLAVERJAS_PLAYERS = 4;
export const KLAVERJAS_CARDS = 8;

/** Card points in the deck, before the ten for the last trick. */
export const CARD_POINTS_TOTAL = 152;
/** Awarded to whoever takes the eighth trick. */
export const LAST_TRICK_BONUS = 10;
/** Everything on the table in one deal: 152 + 10. */
export const DEAL_TOTAL = CARD_POINTS_TOTAL + LAST_TRICK_BONUS;

/** Seven to ace. Every rank below seven is out of the deck. */
export const KLAVERJAS_RANKS: readonly Rank[] = [7, 8, 9, 10, 11, 12, 13, 14];

export type KlaverjasVariant = 'rotterdams' | 'amsterdams';
export type TrumpChoiceMode = 'verplicht' | 'vrij';

export interface KlaverjasOptions {
  variant: KlaverjasVariant;
  /**
   * `verplicht`: the player left of the dealer must name a trump suit.
   * `vrij`: they may pass, and it goes round; if all four pass the dealer must
   * choose. Never a re-deal — a re-deal at a family table is just a pause.
   */
  trumpChoice: TrumpChoiceMode;
  /** 100 for taking all eight tricks. */
  pitBonus: boolean;
  /**
   * Who counts roem.
   *
   * `automatisch`: the app sees it and awards it, which is what it has always
   * done. `zelf`: you have to knock — roem in a trick only counts if someone from
   * the team that took it claims it while that trick is still on the table. That
   * is how it works at a real table, where nobody points out what you missed.
   *
   * The last trick of a deal counts itself: it is scored the moment it lands, so
   * there is no window left to knock in.
   */
  roemClaim: 'automatisch' | 'zelf';
  /** The game ends on whichever of these is reached. */
  length: { kind: 'deals'; deals: number } | { kind: 'points'; target: number };
}

export const DEFAULT_KLAVERJAS_OPTIONS: KlaverjasOptions = {
  variant: 'rotterdams',
  trumpChoice: 'verplicht',
  pitBonus: true,
  // The app counting it is the gentler default, and what every existing table has.
  roemClaim: 'automatisch',
  length: { kind: 'deals', deals: 16 },
};

export const PIT_BONUS = 100;

// --- points and order ------------------------------------------------------

/*
 * The two tables that define the game. Written out per rank rather than
 * derived, because there is no rule underneath them to derive from — they are
 * simply what klaverjassen is, and a clever formula here would be a place for a
 * bug to hide.
 *
 * trump:  B 20 · 9 14 · A 11 · 10 10 · H 4 · V 3 · 8 0 · 7 0   = 62
 * plain:  A 11 · 10 10 · H 4 · V 3 · B 2 · 9 0 · 8 0 · 7 0     = 30
 *
 * 62 + 3 × 30 = 152.
 */
const TRUMP_POINTS: Record<number, number> = {
  11: 20,
  9: 14,
  14: 11,
  10: 10,
  13: 4,
  12: 3,
  8: 0,
  7: 0,
};
const PLAIN_POINTS: Record<number, number> = {
  14: 11,
  10: 10,
  13: 4,
  12: 3,
  11: 2,
  9: 0,
  8: 0,
  7: 0,
};

/** Strength within a suit. Higher wins; the numbers themselves mean nothing. */
const TRUMP_ORDER: Record<number, number> = { 11: 8, 9: 7, 14: 6, 10: 5, 13: 4, 12: 3, 8: 2, 7: 1 };
const PLAIN_ORDER: Record<number, number> = { 14: 8, 10: 7, 13: 6, 12: 5, 11: 4, 9: 3, 8: 2, 7: 1 };

export function cardPoints(card: Card, trump: Suit): number {
  return card.suit === trump ? TRUMP_POINTS[card.rank] : PLAIN_POINTS[card.rank];
}

/** How strong this card is *within its own suit*. Only comparable like for like. */
export function cardStrength(card: Card, trump: Suit): number {
  return card.suit === trump ? TRUMP_ORDER[card.rank] : PLAIN_ORDER[card.rank];
}

export function createKlaverjasDeck(): Card[] {
  const deck: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of KLAVERJAS_RANKS) deck.push({ suit, rank });
  }
  return deck;
}

export interface KlaverjasDeal {
  /** Indexed by seat; eight cards each, the whole deck. */
  hands: Card[][];
}

export function dealKlaverjas(rng: Rng = Math.random): KlaverjasDeal {
  const deck = shuffle(createKlaverjasDeck(), rng);
  const hands: Card[][] = [[], [], [], []];
  deck.forEach((card, index) => hands[index % KLAVERJAS_PLAYERS].push(card));
  return { hands };
}

/**
 * Who takes the trick.
 *
 * Its own function rather than `trickWinner(trick, trump)` from `trick.ts`,
 * which compares `card.rank` numerically. That is right for boerenbridge and
 * hartenjagen and wrong here: in trump the jack (11) beats the ace (14), and in
 * every suit the ten (10) beats the king (13).
 */
export function klaverjasTrickWinner(trick: Trick, trump: Suit): number {
  if (trick.length === 0) throw new Error('cannot determine the winner of an empty trick');

  const led = trick[0].card.suit;
  const trumps = trick.filter((played) => played.card.suit === trump);
  const contenders = trumps.length > 0 ? trumps : trick.filter((p) => p.card.suit === led);

  return contenders.reduce((best, played) =>
    cardStrength(played.card, trump) > cardStrength(best.card, trump) ? played : best
  ).seat;
}

/** Seats 0 and 2 are one team, 1 and 3 the other. */
export function teamOf(seat: number): 0 | 1 {
  return (seat % 2) as 0 | 1;
}

export function partnerOf(seat: number): number {
  return (seat + 2) % KLAVERJAS_PLAYERS;
}

// --- roem ------------------------------------------------------------------

export const ROEM_THREE = 20;
export const ROEM_FOUR = 50;
/** King and queen of trump together. */
export const ROEM_STUK = 20;

/**
 * Roem in a completed trick.
 *
 * Counted per trick and awarded to whichever team takes it, so this runs on the
 * four cards that are on the table and not on anyone's hand.
 *
 * Runs and `stuk` stack: king-queen-jack of trump is a run of three *and* a
 * stuk, which is 40.
 *
 * Note the one place numeric rank *is* correct: a run follows the natural order
 * 7-8-9-10-B-V-H-A, which is exactly ascending rank. The playing order the rest
 * of this file uses has nothing to do with it.
 */
export function roemInTrick(trick: Trick, trump: Suit): number {
  let roem = 0;

  for (const suit of SUITS) {
    const ranks = trick
      .filter((played) => played.card.suit === suit)
      .map((played) => played.card.rank)
      .sort((a, b) => a - b);

    if (ranks.length >= 3) {
      let run = 1;
      let longest = 1;
      for (let i = 1; i < ranks.length; i++) {
        run = ranks[i] === ranks[i - 1] + 1 ? run + 1 : 1;
        longest = Math.max(longest, run);
      }
      if (longest >= 4) roem += ROEM_FOUR;
      else if (longest === 3) roem += ROEM_THREE;
    }
  }

  const hasKing = trick.some((p) => p.card.suit === trump && p.card.rank === 13);
  const hasQueen = trick.some((p) => p.card.suit === trump && p.card.rank === 12);
  if (hasKing && hasQueen) roem += ROEM_STUK;

  return roem;
}

// --- legal plays -----------------------------------------------------------

export interface KlaverjasPlayContext {
  trick: Trick;
  trump: Suit;
  variant: KlaverjasVariant;
  /** The seat about to play, needed to know where its partner is. */
  seat: number;
}

/** Highest trump on the table right now, or 0 if there is none. */
function highestTrumpStrength(trick: Trick, trump: Suit): number {
  const trumps = trick.filter((played) => played.card.suit === trump);
  if (trumps.length === 0) return 0;
  return Math.max(...trumps.map((played) => cardStrength(played.card, trump)));
}

/**
 * Cards that may legally be played.
 *
 * This function *is* the difference between the two variants, and everything in
 * it exists to stop the same thing: quietly getting rid of your losers while
 * your partner does the work.
 */
export function klaverjasLegalPlays(hand: readonly Card[], context: KlaverjasPlayContext): Card[] {
  const { trick, trump, variant, seat } = context;

  // Leading: anything at all.
  if (trick.length === 0) return [...hand];

  const led = trick[0].card.suit;
  const trumpsInHand = hand.filter((card) => card.suit === trump);
  const highest = highestTrumpStrength(trick, trump);

  if (led === trump) {
    /*
     * Trump was led, so trump is simply the suit to follow. On top of that you
     * must beat what is down if you can — otherwise a player holding the jack
     * could sit on it all evening.
     */
    if (trumpsInHand.length === 0) return [...hand];
    const higher = trumpsInHand.filter((card) => cardStrength(card, trump) > highest);
    return higher.length > 0 ? higher : trumpsInHand;
  }

  const following = hand.filter((card) => card.suit === led);
  if (following.length > 0) return following;

  // Void in the led suit. Here the two variants part company.
  if (trumpsInHand.length === 0) return [...hand];

  const partnerHolds = currentWinner(trick, trump) === partnerOf(seat);
  const higher = trumpsInHand.filter((card) => cardStrength(card, trump) > highest);

  if (variant === 'amsterdams') {
    /*
     * Amsterdams: your partner is winning, so you owe the table nothing — throw
     * whatever you like. And if you cannot beat the trump that is down, you may
     * discard rather than waste a trump under it.
     */
    if (partnerHolds) return [...hand];
    return higher.length > 0 ? higher : [...hand];
  }

  /*
   * Rotterdams: you always trump, partner or no partner, and if you cannot
   * overtrump you still have to put a trump under it. Harsher, and the reason
   * a Rotterdams table burns through trumps much faster.
   */
  return higher.length > 0 ? higher : trumpsInHand;
}

export function isLegalKlaverjasPlay(
  card: Card,
  hand: readonly Card[],
  context: KlaverjasPlayContext
): boolean {
  return klaverjasLegalPlays(hand, context).some((legal) => cardsEqual(legal, card));
}

/** Who would take the trick if it stopped right now. */
export function currentWinner(trick: Trick, trump: Suit): number | null {
  return trick.length === 0 ? null : klaverjasTrickWinner(trick, trump);
}

/** A sentence saying which rule refused this card, for the player who tapped it. */
export function whyIllegal(
  card: Card,
  hand: readonly Card[],
  context: KlaverjasPlayContext
): string {
  const { trick, trump, variant, seat } = context;
  if (trick.length === 0) return 'Die kaart mag je nu niet spelen.';

  const led = trick[0].card.suit;
  const trumpsInHand = hand.filter((c) => c.suit === trump);
  const highest = highestTrumpStrength(trick, trump);

  if (led === trump) {
    if (trumpsInHand.length > 0 && card.suit !== trump) return 'Er is troef gevraagd.';
    if (trumpsInHand.some((c) => cardStrength(c, trump) > highest)) {
      return 'Je moet overtroeven als je kunt.';
    }
    return 'Die kaart mag je nu niet spelen.';
  }

  if (hand.some((c) => c.suit === led) && card.suit !== led) return 'Je moet kleur bekennen.';

  if (trumpsInHand.length > 0 && card.suit !== trump) {
    const partnerHolds = currentWinner(trick, trump) === partnerOf(seat);
    if (variant === 'amsterdams' && !partnerHolds) return 'Je moet troeven.';
    if (variant === 'rotterdams') {
      return partnerHolds
        ? 'Rotterdams: je moet troeven, ook als je maat aan slag is.'
        : 'Je moet troeven.';
    }
  }

  if (card.suit === trump && trumpsInHand.some((c) => cardStrength(c, trump) > highest)) {
    return 'Je moet overtroeven als je kunt.';
  }

  return 'Die kaart mag je nu niet spelen.';
}

// --- scoring a deal --------------------------------------------------------

/**
 * A deal seen as two totals, which is how a score sheet sees it.
 *
 * The live game counts cards and tricks; someone keeping score for a physical
 * table only knows what each side ended up with. Both end here, so nat and pit
 * are decided in exactly one place.
 */
export interface DealTotals {
  playingTeam: 0 | 1;
  /** Card points *including* the ten for the last trick. Sums to 162. */
  taken: [number, number];
  roem: [number, number];
  pitBonus: boolean;
}

export interface DealCounts {
  /** Team that chose trump: 0 (seats 0+2) or 1 (seats 1+3). */
  playingTeam: 0 | 1;
  /** Card points each team took, excluding the last-trick bonus. */
  cardPoints: [number, number];
  /** Roem each team collected. */
  roem: [number, number];
  /** Team that took the eighth trick. */
  lastTrickTeam: 0 | 1;
  /** Tricks taken, to spot a pit. */
  tricks: [number, number];
  pitBonus: boolean;
}

/** Named `DealScore`, not `DealResult`: `deck.ts` already exports that. */
export interface DealScore {
  /** Final points added to each team, after nat and pit. */
  points: [number, number];
  /** True when the playing team failed to beat the defenders. */
  nat: boolean;
  /** The team that took all eight tricks, if any. */
  pitTeam: 0 | 1 | null;
  /** What each side had before nat was applied — shown on the score sheet. */
  raw: [number, number];
}

/**
 * Score one deal.
 *
 * The rule that makes klaverjassen klaverjassen: the team that chose trump has
 * to *beat* the other side, roem included. Fall short — even by a single
 * point — and you are "nat": everything on the table goes to the defenders and
 * you score nothing at all.
 */
export function scoreDeal(input: DealCounts): DealScore {
  const { playingTeam, cardPoints: cards, roem, lastTrickTeam, tricks, pitBonus } = input;

  return scoreDealFromTotals({
    playingTeam,
    taken: [
      cards[0] + (lastTrickTeam === 0 ? LAST_TRICK_BONUS : 0),
      cards[1] + (lastTrickTeam === 1 ? LAST_TRICK_BONUS : 0),
    ],
    roem,
    pitBonus,
    // Taking every trick means taking every card, so the totals already say
    // so — but the live game knows for certain, so it is not re-derived.
    tricks,
  });
}

export function scoreDealFromTotals(input: DealTotals & { tricks?: [number, number] }): DealScore {
  const { playingTeam, taken, roem, pitBonus, tricks } = input;
  const defending = (1 - playingTeam) as 0 | 1;

  const raw: [number, number] = [taken[0] + roem[0], taken[1] + roem[1]];

  /*
   * Pit is all eight tricks — which is the same thing as all 162, because
   * taking every card is the only way to get there. A score sheet therefore
   * never has to ask.
   */
  const pitTeam: 0 | 1 | null = tricks
    ? tricks[0] === 8
      ? 0
      : tricks[1] === 8
        ? 1
        : null
    : taken[0] === DEAL_TOTAL
      ? 0
      : taken[1] === DEAL_TOTAL
        ? 1
        : null;

  // Strictly more. Level is not enough — a tie means the playing team is wet.
  const nat = raw[playingTeam] <= raw[defending];

  const points: [number, number] = [0, 0];
  if (nat) {
    // Everything, roem and all, to the other side.
    points[defending] = DEAL_TOTAL + roem[0] + roem[1];
    points[playingTeam] = 0;
  } else {
    points[0] = raw[0];
    points[1] = raw[1];
  }

  if (pitBonus && pitTeam !== null) points[pitTeam] += PIT_BONUS;

  return { points, nat, pitTeam, raw };
}

/** Whether the game is over, given the totals and the chosen length. */
export function klaverjasFinished(
  totals: [number, number],
  dealsPlayed: number,
  length: KlaverjasOptions['length']
): boolean {
  return length.kind === 'deals'
    ? dealsPlayed >= length.deals
    : totals[0] >= length.target || totals[1] >= length.target;
}

/** Highest total wins — the opposite of hartenjagen, and worth being explicit. */
export function klaverjasWinner(totals: [number, number]): 0 | 1 | null {
  if (totals[0] === totals[1]) return null;
  return totals[0] > totals[1] ? 0 : 1;
}

/** Sort a hand for display: by suit, then by playing strength. */
export function sortKlaverjasHand(hand: readonly Card[], trump: Suit): Card[] {
  const suitOrder = (suit: Suit) => (suit === trump ? -1 : SUITS.indexOf(suit));
  return [...hand].sort(
    (a, b) =>
      suitOrder(a.suit) - suitOrder(b.suit) || cardStrength(b, trump) - cardStrength(a, trump)
  );
}
